import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _compact from 'lodash/compact';
import _find from 'lodash/find';
import _maxBy from 'lodash/maxBy';
import _some from 'lodash/some';
import I18n from 'i18n-js';
import {
  GE_ORDER_STATE,
  GE_WEB_MAP_GE_ORDER_STATE,
  GE_WEB_ORDER_STATE,
  ALLOWED_TO_CANCEL_STATES,
  FIRST_TIME_LIMIT_CANCELLABLE_STATES,
  SECOND_TIME_LIMIT_CANCELLABLE_STATES,
  ABOVE_TIME_LIMITS_CANCELLABLE_STATES,
  COMPLETED_ORDER_STATES,
} from 'constants/order-constants';
import { enableCancellationRules } from 'helper/configuration';
import { isPaxUser } from 'helper/grab-id-auth-helper';
import { format as dateFnsFormat, subDays, startOfDay } from 'date-fns';

import { isSameDayDelivery } from './is-same-day-delivery';

export const filterOngoingOrders = orders =>
  orders.filter(order =>
    GE_WEB_MAP_GE_ORDER_STATE[GE_WEB_ORDER_STATE.ActiveDelivery].includes(
      order.status,
    ),
  );

export const checkBookAgainable = order => {
  const steps = _get(order, 'steps', []);
  const isSenderLocUnavailable = isNaN(
    _get(steps[0], 'location.coordinates.latitude'),
  );
  const isRecipientLocUnavailable = steps
    .slice(0)
    .some(step => isNaN(_get(step, 'location.coordinates.latitude')));
  return !isSenderLocUnavailable && !isRecipientLocUnavailable;
};

export const getCreatedTimeToRender = order => {
  const { createAtInTs, createdAt } = _get(order, 'createMeta', {});

  if (!createAtInTs) return createdAt;

  return dateFnsFormat(new Date(createAtInTs * 1000), 'LLL dd, hh:mm aa');
};

export const getOrderStatusToRender = order => {
  const { etaMessage, deliveryID } = order;
  let status = _get(order, 'status', '');
  if (
    status === GE_ORDER_STATE.DroppingOff &&
    isSameDayDelivery(deliveryID) &&
    !_isEmpty(etaMessage)
  ) {
    status = GE_ORDER_STATE.PendingDropOff;
  }

  const geStatustoRender = I18n.t(`v2.orderState.geState.${status}`, {
    defaultValue: '',
  });
  const geWebStatustoRender = I18n.t(`v2.orderState.geWebState.${status}`, {
    defaultValue: '',
  });

  return geStatustoRender || geWebStatustoRender || '--';
};

export const getReceiptRecipients = order => {
  const { steps } = order;
  if (!steps) {
    return [];
  }
  return _compact(
    steps
      .slice(1)
      .reduce(
        (acc, step) =>
          _isEmpty(step.orderReceiptLink) ? acc : acc.concat(step),
        [],
      ),
  );
};

export const getMSDRecipientName = order => {
  const steps = _get(order, 'steps', []);
  const recipients = steps.slice(1);
  const recipientQty = recipients.length;
  const recipientNameSuffix =
    recipientQty > 1
      ? ` ${I18n.t('v2.bookingsPage.andMore', {
          amt: recipientQty - 1,
        })}`
      : '';
  return `${_get(recipients, '[0].contact.name')}${recipientNameSuffix}`;
};

export const isOnGoingOrderCancellable = order => {
  // express/grab-express-webbooking/internal/services/deliveries/api_cancel_deliveries.go:65
  // only the booking user can cancel
  if (!_get(order, 'isCurrentUserDelivery')) {
    return false;
  }
  if (!enableCancellationRules()) {
    if (order.status === GE_WEB_ORDER_STATE.Scheduled) return true;
    const orderEvents = _get(order, 'orderEvents', []);
    const latestOrderEvent = _maxBy(orderEvents, 'Timestamp') || {};
    const latestStatus = latestOrderEvent.State || order.status;
    return Boolean(
      _find(ALLOWED_TO_CANCEL_STATES, state => state === latestStatus),
    );
  }

  const cancelLimitStr1 = _get(order, 'cutOffTime.cancelL1');
  const cancelLimitStr2 = _get(order, 'cutOffTime.cancelL2');

  if (_isEmpty(cancelLimitStr1) || _isEmpty(cancelLimitStr2)) {
    return _find(ALLOWED_TO_CANCEL_STATES, state => state === order.status);
  }

  if (Date.now() <= Date.parse(cancelLimitStr1)) {
    return _find(
      FIRST_TIME_LIMIT_CANCELLABLE_STATES,
      state => state === order.status,
    );
  } else if (Date.now() <= Date.parse(cancelLimitStr2)) {
    return _find(
      SECOND_TIME_LIMIT_CANCELLABLE_STATES,
      state => state === order.status,
    );
  }
  return _find(
    ABOVE_TIME_LIMITS_CANCELLABLE_STATES,
    state => state === order.status,
  );
};

export const fetchPreviousBookings = ({
  fetchPreviousBookingsAction,
  fetchDays,
  fetchLimit,
  companyID,
  startTimestamp,
  endTimestamp,
}) => {
  if (isPaxUser()) {
    if (fetchDays) {
      const startDate = startOfDay(subDays(new Date(), fetchDays));

      return fetchPreviousBookingsAction({
        startTimestamp: startDate.getTime(),
        endTimestamp: 0,
      });
    }
    return fetchPreviousBookingsAction({
      startTimestamp,
      endTimestamp,
    });
  }

  const endDate = new Date();
  const startDate = startOfDay(subDays(endDate, fetchDays));
  const fromDate = dateFnsFormat(startDate, 'yyyy-LL-dd');
  const toDate = dateFnsFormat(endDate, 'yyyy-LL-dd');
  const states = COMPLETED_ORDER_STATES.find(
    s => s.state === GE_WEB_ORDER_STATE.AllStatus,
  ).stateToQuery;
  return fetchPreviousBookingsAction({
    companyID,
    fromDate,
    toDate,
    states,
    limit: fetchLimit,
  });
};

export const getDisplayDeliveryType = booking => {
  if (!booking) {
    return undefined;
  }

  const { service, serviceDisplayInfo, steps } = booking;

  const isCOD = _some(
    steps,
    step =>
      _get(step, 'cashOnDelivery.enable') &&
      _get(step, 'cashOnDelivery.amount', 0) > 0,
  );

  if (serviceDisplayInfo) {
    const { serviceType, specialServiceType, vehicleType } = serviceDisplayInfo;
    if (serviceType) {
      let displayService = serviceType;
      if (specialServiceType) {
        displayService += ` - ${specialServiceType}`;
      }
      if (vehicleType) {
        displayService += ` - ${vehicleType}`;
      }
      if (isCOD) {
        displayService += ` - ${I18n.t('history.cod')}`;
      }
      return displayService;
    }
  }

  return _get(service, 'serviceName');
};

export default {};
